import { useState } from 'react';
import {
  MapPin, Search, Users, Building2, Mail, Clock, Handshake, Scale,
  ChevronDown, ExternalLink, AlertTriangle,
} from 'lucide-react';
import { Badge, Card, CardHeader, cn } from '@/components/ui';

/**
 * How to find leads, approach them and convert them.
 *
 * ── Why this is a page in the product rather than a document somewhere ────
 *
 * This tab used to search for businesses automatically. That was three paid
 * services to keep alive for a job that takes a person twenty minutes and
 * produces a better list — because a person can tell a letting agent from a
 * solicitor who mentions property, and can tell a limited company from a sole
 * trader, which is the difference between a cold email that is lawful and one
 * that is not.
 *
 * What was actually missing was never the search. It was knowing where to
 * look, what to say, and when to stop. So that is what the product supplies
 * now, and it lives here — next to the empty list, where somebody wondering
 * what to do about it is actually standing.
 *
 * Everything below is specific on purpose. "Do outreach" is not advice;
 * "Rightmove's find-an-agent page, filtered to lettings, one town at a time"
 * is something you can do this afternoon.
 */

interface Source {
  name: string;
  what: string;
  url?: string;
  gives: string;
}

/** Where UK letting agents and portfolio landlords can actually be found. */
const SOURCES: { group: string; icon: typeof MapPin; blurb: string; items: Source[] }[] = [
  {
    group: 'Public listings',
    icon: Search,
    blurb: 'Every agent who lets property advertises it. Start where they advertise.',
    items: [
      {
        name: 'Rightmove — find an agent',
        what: 'Filter to lettings and work one town at a time. Independents are '
          + 'the ones worth writing to; a corporate chain has software already.',
        url: 'https://www.rightmove.co.uk/estate-agents.html',
        gives: 'Name, branch address, phone, website',
      },
      {
        name: 'Zoopla — find agents',
        what: 'Same job, different set. Agents who are on one and not the other '
          + 'are often the smaller ones.',
        url: 'https://www.zoopla.co.uk/find-agents/',
        gives: 'Name, address, website',
      },
      {
        name: 'OnTheMarket',
        what: 'Agent-owned, so it skews independent — which is the half of the '
          + 'market this product is for.',
        url: 'https://www.onthemarket.com/agents/',
        gives: 'Name, website',
      },
      {
        name: 'Google Maps',
        what: '"letting agents in <town>". Look at who has a real office and a '
          + 'handful of reviews rather than hundreds.',
        gives: 'Name, address, phone, website, opening hours',
      },
    ],
  },
  {
    group: 'Official registers',
    icon: Building2,
    blurb: 'Free, authoritative, and the only sources that tell you what kind of '
      + 'business you are writing to.',
    items: [
      {
        name: 'Companies House — advanced search',
        what: 'Filter by SIC code and location. 68209 is letting and operating '
          + 'of own or leased property; 68320 is management of property on a fee '
          + 'or contract basis. Active companies incorporated in the last few '
          + 'years are the ones still choosing their tools.',
        url: 'https://find-and-update.company-information.service.gov.uk/advanced-search',
        gives: 'Legal name, company number, registered address, SIC code',
      },
      {
        name: 'Council HMO licence registers',
        what: 'Every council publishes one. It lists the licence holder for each '
          + 'HMO — which is a landlord or agent managing shared houses, by name, '
          + 'with how many they run. Search "<council> HMO public register".',
        gives: 'Name, property address, portfolio size',
      },
      {
        name: 'Selective licensing registers',
        what: 'Where a council operates selective licensing, the same again for '
          + 'ordinary lets. Best single source of landlords with several '
          + 'properties in one place.',
        gives: 'Name, addresses',
      },
      {
        name: 'Property redress schemes',
        what: 'Agents must belong to one. The Property Redress Scheme and '
          + 'Property Ombudsman both publish member directories.',
        gives: 'Name, trading address, membership',
      },
    ],
  },
  {
    group: 'Where they gather',
    icon: Users,
    blurb: 'Slower, warmer, and converts far better than anything cold.',
    items: [
      {
        name: 'NRLA branch meetings',
        what: 'The National Residential Landlords Association runs local '
          + 'meetings. Landlords with enough properties to have a problem worth '
          + 'solving are the ones who turn up.',
        url: 'https://www.nrla.org.uk/',
        gives: 'A conversation, which is worth twenty cold emails',
      },
      {
        name: 'Property investor meetups',
        what: 'Search Meetup and Eventbrite for "property investor <city>". '
          + 'Portfolio landlords, mid-growth, usually on a spreadsheet.',
        gives: 'Introductions',
      },
      {
        name: 'Local Facebook and WhatsApp landlord groups',
        what: 'Read for a fortnight before you post anything. Answer questions '
          + 'about deposits and licensing without mentioning your product, and '
          + 'people will ask what you do.',
        gives: 'Reputation, then leads',
      },
    ],
  },
  {
    group: 'People you already know',
    icon: Handshake,
    blurb: 'The highest-converting source there is, and the one everybody skips.',
    items: [
      {
        name: 'Ask your customers',
        what: 'A landlord who likes PROVIA knows three others. Ask at the point '
          + 'they say something nice — after a good support reply, not in a '
          + 'newsletter.',
        gives: 'A warm introduction',
      },
      {
        name: 'Your own lettings contacts',
        what: 'Anyone you have rented from, or who manages a block you know. '
          + 'They will at least tell you honestly why they would not switch.',
        gives: 'Feedback, sometimes a customer',
      },
      {
        name: 'Accountants and mortgage brokers who work with landlords',
        what: 'They talk to dozens of small landlords and are asked for tool '
          + 'recommendations constantly. Worth one coffee each.',
        gives: 'Repeat referrals',
      },
    ],
  },
];

/** The qualifying questions, in the order they save you time. */
const QUALIFY = [
  {
    q: 'Are they a limited company or a sole trader?',
    why: 'This decides whether a cold email to them is lawful at all, so it '
      + 'comes first. A limited company prints its registered number on its '
      + 'website, usually in the footer — that is the check. No number and a '
      + 'personal name over the door usually means a sole trader.',
    action: 'Set it on the lead. Sole traders are flagged in red on the list.',
    critical: true,
  },
  {
    q: 'Do they actually let or manage residential property?',
    why: 'A search for letting agents returns solicitors, surveyors, builders '
      + 'and block managers who do nothing PROVIA helps with. Thirty seconds on '
      + 'their site saves a wasted email and a slightly annoyed stranger.',
    action: 'If not, delete the lead rather than leaving it to be sent to later.',
  },
  {
    q: 'How many properties?',
    why: 'Under about five and a spreadsheet genuinely is fine — they will not '
      + 'switch and they will be right not to. Over a few hundred and they have '
      + 'an enterprise system and a contract. The middle is the whole market.',
    action: 'Put the number in the notes. It decides which plan you talk about.',
  },
  {
    q: 'What are they using now?',
    why: 'A spreadsheet is the easiest conversation you will ever have. Existing '
      + 'software means you need a reason to switch, and "cheaper" is the '
      + 'weakest one.',
    action: 'Look for a tenant portal link on their site — it tells you.',
  },
];

/** First contact, follow-up, and when to stop. */
const APPROACH = [
  {
    step: 'The first email',
    icon: Mail,
    points: [
      'Five sentences. Anything longer is read as a circular and deleted.',
      'Name where you found them in the first line — "I saw you manage the '
        + 'Kirkstall Road HMOs" beats "Dear Sir/Madam" by a mile.',
      'One specific thing PROVIA does that matches them. Not a feature list: '
        + 'a landlord with four HMOs cares about splitting a utility bill, not '
        + 'about your dashboard.',
      'Ask for a reply, not a meeting. "Is chasing rent actually a problem for '
        + 'you?" gets answered; "Can I book 30 minutes?" does not.',
      'No attachment, no PDF, one link at most.',
    ],
  },
  {
    step: 'Following up',
    icon: Clock,
    points: [
      'Once, about a week later, and that is the end of it. Two follow-ups is '
        + 'persistence; four is a complaint.',
      'Add something rather than repeating yourself — a link to the tour, or an '
        + 'answer to the objection you expect.',
      'Mark them lost and move on. A lead that goes quiet twice is not a lead, '
        + 'and the list is more useful without them in it.',
      'PROVIA will not include an already-contacted lead in a whole-status send '
        + 'for exactly this reason. Picking them by hand overrides it, because '
        + 'that is a deliberate follow-up.',
    ],
  },
  {
    step: 'Turning a reply into a trial',
    icon: Handshake,
    points: [
      'Send the tour link before you offer a call. Most people would rather look '
        + 'than talk, and the ones who book after looking are the serious ones.',
      'Offer to import what they have. The objection is never price, it is the '
        + 'forty tenancies already in a spreadsheet — and PROVIA imports those, '
        + 'showing them row by row what will happen before it writes anything.',
      'Get one real property in during the first conversation. A trial with no '
        + 'data in it converts at nothing.',
      'Mark them won and set the organisation on the lead, so the account and '
        + 'the conversation that produced it stay joined up.',
    ],
  },
];

/** Objections you will actually hear, and what is true about them. */
const OBJECTIONS = [
  {
    heard: '"I just use a spreadsheet."',
    answer: 'Agree — for five properties it is fine. Ask what happens when a '
      + 'tenant says they paid and the bank statement does not show it. The '
      + 'verification step is the answer, and it is not something a spreadsheet '
      + 'can do.',
  },
  {
    heard: '"I do not want to move all my data."',
    answer: 'Properties, rooms, tenants and tenancies import from any CSV, with '
      + 'a row-by-row preview first. Say plainly that the rent ledger does not '
      + 'import, and why: a history assembled from another system with no way to '
      + 'check it is how a tenant gets chased for rent they already paid.',
  },
  {
    heard: '"My tenants will never use a portal."',
    answer: 'They sign in with their phone number and a texted code — no '
      + 'password. And the landlord does not need them to: everything works '
      + 'whether or not a single tenant ever logs in.',
  },
  {
    heard: '"It is another monthly cost."',
    answer: 'It is. One tenancy going a month unchased costs more. Do not '
      + 'discount on the first call — offer the trial instead, which costs them '
      + 'nothing and proves it or does not.',
  },
  {
    heard: 'Silence.',
    answer: 'The most common answer, and not a rejection. Follow up once, then '
      + 'mark them lost. Nothing about a quiet lead improves by writing again.',
  },
];

export default function LeadPlaybook() {
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Finding leads"
          subtitle="PROVIA does not search for you. Here is where to look — an hour here fills the list for a month."
        />
        <div className="p-5 sm:p-6 pt-4 space-y-6">
          {SOURCES.map(({ group, icon: Icon, blurb, items }) => (
            <div key={group}>
              <div className="flex items-center gap-2 mb-1">
                <Icon className="h-4 w-4 text-brand" />
                <h3 className="font-semibold text-ink">{group}</h3>
              </div>
              <p className="text-sm text-muted mb-3 leading-relaxed">{blurb}</p>
              <ul className="space-y-3">
                {items.map((item) => (
                  <li key={item.name} className="rounded-xl border border-line p-3">
                    <div className="flex items-start justify-between gap-3">
                      <p className="font-medium text-ink text-sm">
                        {item.url ? (
                          <a
                            href={item.url}
                            target="_blank"
                            rel="noreferrer noopener"
                            className="hover:text-brand transition-colors inline-flex items-center gap-1.5"
                          >
                            {item.name}
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        ) : item.name}
                      </p>
                      <Badge tone="neutral">{item.gives}</Badge>
                    </div>
                    <p className="text-sm text-muted mt-1.5 leading-relaxed">{item.what}</p>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </Card>

      <Card>
        <CardHeader
          title="Before you write to anybody"
          subtitle="Four questions. The first one is not optional."
        />
        <div className="p-5 sm:p-6 pt-4 space-y-3">
          {QUALIFY.map((item) => (
            <div
              key={item.q}
              className={cn(
                'rounded-xl border p-3.5',
                item.critical ? 'border-warn/30 bg-warn/5' : 'border-line',
              )}
            >
              <p className="font-medium text-ink flex items-start gap-2">
                {item.critical && <Scale className="h-4 w-4 text-warn shrink-0 mt-0.5" />}
                {item.q}
              </p>
              <p className="text-sm text-muted mt-1.5 leading-relaxed">{item.why}</p>
              <p className="text-sm text-ink/80 mt-2">→ {item.action}</p>
            </div>
          ))}
        </div>
      </Card>

      <Card>
        <CardHeader title="Making contact" subtitle="Short, specific, and it stops after two" />
        <div className="p-5 sm:p-6 pt-4 space-y-6">
          {APPROACH.map(({ step, icon: Icon, points }) => (
            <div key={step}>
              <div className="flex items-center gap-2 mb-2">
                <Icon className="h-4 w-4 text-brand" />
                <h3 className="font-semibold text-ink">{step}</h3>
              </div>
              <ul className="space-y-1.5">
                {points.map((point) => (
                  <li key={point} className="text-sm text-muted leading-relaxed flex gap-2">
                    <span className="text-faint shrink-0">·</span>
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </Card>

      <Card>
        <CardHeader title="What you will hear back" />
        <div className="p-5 sm:p-6 pt-4 space-y-3">
          {OBJECTIONS.map((item) => (
            <Collapsible key={item.heard} summary={item.heard}>
              {item.answer}
            </Collapsible>
          ))}
        </div>
      </Card>

      {/*
        Last, and not buried in a paragraph.

        Every other panel on this page is advice you can take or leave. This
        one is the law, and getting it wrong is not a lost sale — it is a
        complaint to the ICO and a sending domain that every landlord's tenant
        mail also depends on.
      */}
      <Card>
        <CardHeader title="The law, briefly" subtitle="UK — a summary, not advice" />
        <div className="p-5 sm:p-6 pt-4 space-y-3 text-sm text-muted leading-relaxed">
          <div className="flex items-start gap-2 text-warn bg-warn/10 border border-warn/25
                          rounded-xl px-3 py-2.5">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>
              Marketing email to a <strong>corporate subscriber</strong> — a limited
              company, an LLP, a public body — is allowed under PECR without prior
              consent. A <strong>sole trader or unincorporated partnership</strong> is
              treated as an individual and needs consent or the soft opt-in.
              That is why the company type is the first thing you establish.
            </span>
          </div>
          <p>
            Every message must identify who sent it, give a valid address, and offer a
            way to opt out. PROVIA does all three for you: the company name is in the
            footer, mail leaves from an authenticated domain, and the unsubscribe line
            is attached by the code rather than by whoever wrote the template — so it
            cannot be left off one.
          </p>
          <p>
            An opt-out is honoured immediately and permanently, and a lead who opts out
            cannot be deleted. That is deliberate: deleting them is how the next person
            to go looking finds the same business and writes to them again.
          </p>
          <p>
            Keeping contact details of people at those businesses is processing personal
            data under UK GDPR. Legitimate interests is the usual basis for B2B
            prospecting; it needs a balancing test you have actually done, and a privacy
            notice that says you do this.
          </p>
        </div>
      </Card>
    </div>
  );
}

function Collapsible({ summary, children }: { summary: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-line overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-3.5 py-3 text-left
                   hover:bg-raised/50 transition-colors"
      >
        <span className="text-sm font-medium text-ink italic">{summary}</span>
        <ChevronDown
          className={cn('h-4 w-4 text-faint shrink-0 transition-transform', open && 'rotate-180')}
        />
      </button>
      {open && (
        <p className="px-3.5 pb-3.5 text-sm text-muted leading-relaxed">{children}</p>
      )}
    </div>
  );
}
